import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Header from '@/components/Header';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Star, Clock, Users, Filter, Search } from 'lucide-react';
import { toast } from 'sonner';

const ClassesPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    sport: searchParams.get('sport') || 'all',
    skill_level: 'all',
    search: ''
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.sport && filters.sport !== 'all') params.append('sport', filters.sport);
      if (filters.skill_level && filters.skill_level !== 'all') params.append('skill_level', filters.skill_level);
      
      const response = await axios.get(`${API}/classes?${params}`);
      setClasses(response.data);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = classes.filter(cls =>
    cls.title.toLowerCase().includes(filters.search.toLowerCase()) ||
    cls.sport.toLowerCase().includes(filters.search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="classes-page">
      <SEO
        title="Courses"
        description="Explore online courses at Sun Kamal Academy. Learn Chess, Tabla, Yoga, Drums and more with expert instructors."
        path="/classes"
      />
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-10 sm:py-16 pt-24 sm:pt-28">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 sm:mb-4" data-testid="page-title">Explore Courses</h1>
          <p className="text-base sm:text-lg md:text-xl opacity-90">Find the perfect class for your learning journey</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 md:py-12">
        {/* Filters */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm mb-6 sm:mb-8" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-base sm:text-lg font-semibold">Filters</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <Input
                placeholder="Search classes..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full"
                data-testid="search-input"
              />
            </div>
            <Select value={filters.sport} onValueChange={(value) => setFilters({ ...filters, sport: value })}>
              <SelectTrigger data-testid="sport-filter">
                <SelectValue placeholder="All Courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                <SelectItem value="Chess">Chess</SelectItem>
                <SelectItem value="Tabla">Tabla</SelectItem>
                <SelectItem value="Drums">Drums</SelectItem>
                <SelectItem value="Yoga">Yoga</SelectItem>
                <SelectItem value="Checkers">Checkers</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.skill_level} onValueChange={(value) => setFilters({ ...filters, skill_level: value })}>
              <SelectTrigger data-testid="skill-level-filter">
                <SelectValue placeholder="Skill Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchClasses} className="btn-primary" data-testid="apply-filters-btn">
              Apply Filters
            </Button>
          </div>
        </div>

        {/* Classes Grid */}
        {loading ? (
          <div className="text-center py-16 sm:py-20" data-testid="loading-state">
            <div className="text-lg sm:text-xl text-gray-600">Loading classes...</div>
          </div>
        ) : filteredClasses.length === 0 ? (
          <div className="text-center py-16 sm:py-20" data-testid="empty-state">
            <div className="text-lg sm:text-xl text-gray-600">No classes found</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="classes-grid">
            {filteredClasses.map((cls, index) => (
              <Card
                key={cls.id}
                className="overflow-hidden hover:shadow-xl transition cursor-pointer"
                onClick={() => navigate(`/classes/${cls.id}`)}
                data-testid={`class-card-${index}`}
              >
                <img
                  src={cls.image}
                  alt={cls.title}
                  className="w-full h-40 sm:h-48 object-cover"
                  loading="lazy"
                />
                <div className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 sm:px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-xs sm:text-sm font-medium">
                      {cls.sport}
                    </span>
                    <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm font-medium">
                      {cls.skill_level}
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2" data-testid={`class-title-${index}`}>{cls.title}</h3>
                  <p className="text-gray-600 mb-3 sm:mb-4 line-clamp-2 text-sm">{cls.description}</p>
                  <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {cls.duration} min
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {cls.enrolled_count}/{cls.max_students}
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {cls.rating.toFixed(1)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xl sm:text-2xl font-bold text-orange-600">${cls.price}</div>
                    <Button className="btn-primary text-sm" data-testid={`view-class-btn-${index}`}>
                      View Details
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassesPage;
